import React from "react";
import { Button } from "@/components/ui/button";
import { AlertTriangle, RefreshCw } from "lucide-react";

/**
 * Fase 18 · Error Boundary con UI elegante en lugar de pantalla roja.
 *
 * Envuelve páginas del dashboard (Conectividad, módulos) para que un fallo en
 * un solo componente no tumbe toda la app. Muestra "Algo salió mal" con botón
 * Reintentar (reset del boundary) y un botón para recargar la página completa.
 *
 * Uso:
 *   <ErrorBoundary section="Centro de Conectividad">
 *     <Connectivity />
 *   </ErrorBoundary>
 */
export default class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }
  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }
  componentDidCatch(error, info) {
    // eslint-disable-next-line no-console
    console.error("[fstudy][error-boundary]", this.props.section || "app", error, info);
  }
  reset = () => this.setState({ hasError: false, error: null });
  reload = () => { window.location.reload(); };

  render() {
    if (!this.state.hasError) return this.props.children;
    const label = this.props.section || "esta sección";
    return (
      <div className="min-h-[420px] flex items-center justify-center p-6" data-testid="error-boundary">
        <div className="card-elevated p-10 max-w-lg w-full text-center space-y-4">
          <div className="mx-auto h-14 w-14 rounded-full bg-amber-50 border border-amber-200 flex items-center justify-center">
            <AlertTriangle className="h-7 w-7 text-amber-600" />
          </div>
          <h2 className="font-serif-display text-2xl text-brand-ink">
            Algo salió mal al cargar {label}
          </h2>
          <p className="text-sm text-stone-500">
            Puedes reintentar sin recargar. Si el problema persiste, recarga la página o reporta al equipo.
          </p>
          {this.state.error?.message && (
            <div className="text-xs text-stone-400 font-mono break-all bg-stone-50 border border-stone-200 rounded-lg p-3 max-h-32 overflow-y-auto">
              {String(this.state.error.message).slice(0, 400)}
            </div>
          )}
          <div className="flex flex-col sm:flex-row justify-center gap-2 pt-2">
            <Button onClick={this.reset} className="btn-primary" data-testid="error-boundary-retry">
              <RefreshCw className="h-4 w-4 mr-1.5" /> Reintentar
            </Button>
            <Button variant="outline" onClick={this.reload} data-testid="error-boundary-reload">
              Recargar página
            </Button>
          </div>
        </div>
      </div>
    );
  }
}
